/**
 * Inner content for Voluum lander Tracking tab accordion steps.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Copy } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Input } from '@/components/ui/form/input/Input';
import type { VoluumLanderFormValues } from '../map-voluum-lander-form';

const labelCls = 'text-xs font-semibold text-muted-foreground';
const fieldCls = 'space-y-1';

export interface LanderTrackingCtaStepBodyProps {
  values: VoluumLanderFormValues;
  patch: (p: Partial<VoluumLanderFormValues>) => void;
  disabled?: boolean;
  clickUrl: string;
  onCopy: () => void;
}

export const LanderTrackingCtaStepBody: FC<LanderTrackingCtaStepBodyProps> = ({
  values,
  patch,
  disabled,
  clickUrl,
  onCopy,
}) => (
  <>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-cta-count">
        Number of CTAs
      </label>
      <Input
        id="lander-cta-count"
        type="number"
        min={1}
        disabled={disabled}
        value={values.numberOfOffers}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          patch({ numberOfOffers: Math.max(1, Math.round(n)) });
        }}
        className="h-8"
      />
    </div>
    <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-foreground">
      <li>Set the number of CTA buttons that need a click URL.</li>
      <li>
        Copy the click URL and paste it into each CTA link in your lander HTML.
      </li>
      <li>
        Ensure links appear inside the{' '}
        <code className="font-mono">&lt;body&gt;</code>.
      </li>
    </ol>
    <div className="flex gap-2">
      <Input
        readOnly
        value={clickUrl || '—'}
        className="h-8 flex-1 font-mono text-xs"
        aria-label="Voluum click URL"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        disabled={disabled || !clickUrl}
        onClick={() => void onCopy()}
        aria-label="Copy click URL"
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  </>
);

export const LanderTrackingScriptStepBody: FC = () => (
  <>
    <p className="text-xs text-muted-foreground">
      Paste the Voluum lander tracking script at the bottom of your page&apos;s{' '}
      <code className="font-mono">&lt;head&gt;</code>. The full script is shown
      in Voluum when this lander uses direct tracking — open the lander there to
      copy the exact snippet for your tracking domain.
    </p>
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
      Enable <strong className="text-foreground">Lander tracking script</strong>{' '}
      above and save in Voluum to align with your live lander configuration.
    </div>
  </>
);
